import type { BackupStatus } from '@/services/backupStatusService';

export type BackupDotState = 'green' | 'orange' | 'red' | 'gray' | 'unknown';

export interface BackupDotView {
  state: BackupDotState;
  /** Texte de l'infobulle (une information par ligne) */
  title: string;
  /** Texte court pour les lecteurs d'écran */
  label: string;
}

const TZ = 'Europe/Brussels';

const dayKey = (d: Date) =>
  new Intl.DateTimeFormat('fr-BE', {
    timeZone: TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(d);

/** « 14:20 » aujourd'hui, « 02/11 14:20 » un autre jour (heure de Bruxelles). */
export function formatWhen(iso: string | null, now: Date): string {
  if (!iso) return 'inconnue';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return 'inconnue';
  const time = new Intl.DateTimeFormat('fr-BE', {
    timeZone: TZ,
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).format(d);
  if (dayKey(d) === dayKey(now)) return time;
  const date = new Intl.DateTimeFormat('fr-BE', {
    timeZone: TZ,
    day: '2-digit',
    month: '2-digit',
  }).format(d);
  return `${date} ${time}`;
}

/** « 12 min », « 3 h », « 2 j » */
export function formatAge(minutes: number): string {
  const m = Math.max(0, Math.round(minutes));
  if (m < 60) return `${m} min`;
  if (m < 24 * 60) return `${Math.floor(m / 60)} h`;
  return `${Math.floor(m / 1440)} j`;
}

const GB = new Intl.NumberFormat('fr-BE', { maximumFractionDigits: 1 });

/**
 * Apparence du voyant. unreachable = le backend ne répond pas (deux échecs de suite) : pastille grise en pointillés,
 * avec l'âge de la dernière donnée connue s'il y en a une. Jamais un vert périmé.
 */
export function describeBackupStatus(opts: {
  data: BackupStatus | undefined;
  unreachable: boolean;
  dataUpdatedAt?: number;
  now: Date;
}): BackupDotView {
  const { data, unreachable, now } = opts;

  if (unreachable || !data) {
    const lines = ['Sauvegardes : état inconnu', 'Serveur d’état injoignable'];
    if (data && opts.dataUpdatedAt) {
      lines.push(
        `Dernière donnée reçue il y a ${formatAge((now.getTime() - opts.dataUpdatedAt) / 60000)}`
      );
    }
    return { state: 'unknown', title: lines.join('\n'), label: 'Sauvegardes : état inconnu' };
  }

  const lines = [`Sauvegardes : ${data.summary}`];
  for (const c of data.checks) lines.push(c.label);
  if (data.tasks.length > 0) {
    for (const t of data.tasks)
      lines.push(`${t.label} : dernière réussite ${formatWhen(t.lastOk, now)}`);
  }
  if (data.freeGb !== null) lines.push(`Disque D: : ${GB.format(data.freeGb)} Go libres`);
  if (data.releveAgeMin !== null)
    lines.push(`Relevé du PC : il y a ${formatAge(data.releveAgeMin)}`);

  return {
    state: data.level,
    title: lines.join('\n'),
    label: `Sauvegardes : ${data.summary}`,
  };
}
